import { useEffect, useRef, useState } from "react";
import Popover from "@mui/material/Popover";
import { toast } from "sonner";

import { RESEND_INTERVIEW } from "../../../urls";
import ConfirmationModal from "../../../../components/ConfirmationModal/ConfirmationModal";
import { getModalConfig } from "../../../../components/ConfirmationModal/modalHelper";
import Button from "../button/Button";
import Tooltip from "../tooltip/Tooltip";

const CLOSE_DELAY_MS = 150;

export default function OutdatedQuestionsBadge({
  applicant,
  companyId,
  jobId,
  token,
  disabled = false,
  onResent,
}) {
  const session = applicant?.interview_session || applicant;
  const [anchorEl, setAnchorEl] = useState(null);
  const [firstModalOpen, setFirstModalOpen] = useState(false);
  const [finalModalOpen, setFinalModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const closeTimer = useRef(null);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  if (!session?.questions_outdated) return null;

  const frozenVersion = session.frozen_questions_version;
  const currentVersion = applicant?.job?.questions_version;
  const questionTexts = session.frozen_question_texts || [];
  const candidateName = applicant?.name || "this candidate";

  const keepOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  };

  const openFirstConfirmation = () => {
    if (disabled) return;
    setAnchorEl(null);
    setFirstModalOpen(true);
  };

  const continueToFinalConfirmation = () => {
    setFirstModalOpen(false);
    setFinalModalOpen(true);
  };

  const resendWithUpdatedQuestions = async () => {
    if (!companyId || !jobId || !applicant?.id || !token) return;

    setIsLoading(true);
    try {
      const response = await fetch(
        RESEND_INTERVIEW(companyId, jobId, applicant.id),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: "updated_questions" }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to resend interview");
      }

      setFinalModalOpen(false);
      toast.success(`Interview resent successfully to ${candidateName}`);
      if (onResent) await onResent();
    } catch (error) {
      console.error("Error restarting interview with updated questions:", error);
      toast.error(
        error.message || "Failed to resend interview. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <span
        className="inline-flex cursor-default items-center rounded-full bg-red-100 px-2 py-0.5 text-body font-semibold text-red-700"
        onMouseEnter={(event) => {
          keepOpen();
          setAnchorEl(event.currentTarget);
        }}
        onMouseLeave={scheduleClose}
        aria-label={`Interview used outdated question set version ${frozenVersion}`}
      >
        v{frozenVersion}
      </span>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        sx={{ pointerEvents: "none" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            onMouseEnter: keepOpen,
            onMouseLeave: scheduleClose,
            className: "mt-1 w-96 max-w-[calc(100vw-2rem)] p-4",
            sx: { pointerEvents: "auto" },
          },
        }}
      >
        <div className="text-small font-semibold text-gray-900">
          Current questions: v{currentVersion} · This interview used v{frozenVersion}
        </div>
        <ol className="my-3 max-h-56 list-decimal space-y-2 overflow-y-auto pl-5 text-small text-gray-700">
          {questionTexts.map((question, index) => (
            <li key={`${index}-${question}`}>{question}</li>
          ))}
        </ol>
        <Tooltip
          content="Restarts this candidate's interview. Their current answers and evaluation will be deleted."
          disabled={disabled}
        >
          <Button
            variant="danger"
            disabled={disabled}
            onClick={openFirstConfirmation}
          >
            Resend interview with new questions
          </Button>
        </Tooltip>
      </Popover>

      <ConfirmationModal
        isOpen={firstModalOpen}
        onClose={() => setFirstModalOpen(false)}
        onConfirm={continueToFinalConfirmation}
        {...getModalConfig(
          "resendUpdatedQuestions",
          1,
          "Interview",
          candidateName
        )}
        confirmButtonVariant="danger"
      />

      <ConfirmationModal
        isOpen={finalModalOpen}
        onClose={() => setFinalModalOpen(false)}
        onConfirm={resendWithUpdatedQuestions}
        {...getModalConfig(
          "resendUpdatedQuestionsFinal",
          1,
          "Interview",
          candidateName
        )}
        confirmButtonVariant="danger"
        isLoading={isLoading}
        loadingText="Restarting..."
      />
    </>
  );
}
